import { authService } from "../../../utils/authService";
import { getAndClearOAuthReturnPath } from "../../../utils/oauthReturnHandler";
import { savePhoneSession } from "../../../utils/phoneSession";
import { ROUTES } from "../../../components/shared/constant";

/** Calls behind the sign-in page's phone form (no sign-in needed). */

export interface PhoneSignInSettings {
  enabled: boolean;
  countries: Array<{ code: string; callingCode: string }>;
  codeLength: number;
  resendSeconds: number;
}

const OFF: PhoneSignInSettings = {
  enabled: false,
  countries: [],
  codeLength: 6,
  resendSeconds: 30,
};
const TIMEOUT_MS = 15_000;

type Answer = { ok: boolean; status: number; data: Record<string, unknown> };

async function call(
  method: "GET" | "POST",
  url: string,
  body?: unknown,
  token?: string,
): Promise<Answer> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const res = await fetch(url, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    // AbortSignal.timeout isn't in older Safari (iOS 15 and before).
    signal: controller.signal,
  }).finally(() => clearTimeout(timer));
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: res.ok, status: res.status, data };
}

function messageOf(answer: Answer, fallback: string): string {
  return typeof answer.data.error === "string" && answer.data.error ? answer.data.error : fallback;
}

/** Whether, and how, phone sign-in is offered. Off on any failure: the page still shows the other buttons. */
export async function loadPhoneSignInSettings(): Promise<PhoneSignInSettings> {
  try {
    const answer = await call("GET", "/api/v1/auth/phone/config");
    if (!answer.ok) return OFF;
    const d = answer.data as Partial<PhoneSignInSettings>;
    const countries = Array.isArray(d.countries)
      ? d.countries.filter(
          (c): c is { code: string; callingCode: string } =>
            typeof c?.code === "string" && typeof c?.callingCode === "string",
        )
      : [];
    return {
      enabled: d.enabled === true && countries.length > 0,
      countries,
      codeLength: typeof d.codeLength === "number" ? d.codeLength : OFF.codeLength,
      resendSeconds: typeof d.resendSeconds === "number" ? d.resendSeconds : OFF.resendSeconds,
    };
  } catch {
    return OFF;
  }
}

export type CodeRequest =
  | { ok: true; resendIn: number }
  | { ok: false; error: string; retryIn?: number };

export async function requestCode(phone: string, country: string): Promise<CodeRequest> {
  try {
    const answer = await call("POST", "/api/v1/auth/phone/request", { phone, country });
    if (!answer.ok) {
      const retryIn = typeof answer.data.retryIn === "number" ? answer.data.retryIn : undefined;
      return {
        ok: false,
        error: messageOf(answer, "We couldn't text a code. Please try again."),
        retryIn,
      };
    }
    const resendIn = typeof answer.data.resendIn === "number" ? answer.data.resendIn : 0;
    return { ok: true, resendIn };
  } catch {
    return {
      ok: false,
      error: "We couldn't reach skySupport. Check your connection and try again.",
    };
  }
}

export type CodeCheck =
  | { ok: true; token: string }
  | { ok: false; error: string; askAgain: boolean };

export async function checkCode(phone: string, country: string, code: string): Promise<CodeCheck> {
  try {
    const answer = await call("POST", "/api/v1/auth/phone/verify", { phone, country, code });
    if (answer.ok && typeof answer.data.token === "string") {
      return { ok: true, token: answer.data.token };
    }
    const reason = answer.data.code;
    return {
      ok: false,
      error: messageOf(answer, "That didn't work. Please try again."),
      // An expired or used-up code can't be retried: start again with a new one.
      askAgain: reason === "EXPIRED" || reason === "USED_UP",
    };
  } catch {
    return {
      ok: false,
      error: "We couldn't reach skySupport. Check your connection and try again.",
      askAgain: false,
    };
  }
}

/**
 * Finishes a phone sign-in the way /callback finishes a Microsoft one: opens
 * the session (POST /api/user/session), keeps the pass, then loads the page
 * the person was going to (or home). A full page load, so the whole app starts
 * fresh as this person. Returns an error message when it can't.
 */
export async function finishPhoneSignIn(token: string): Promise<string | null> {
  let answer: Answer;
  try {
    answer = await call("POST", "/api/user/session", {}, token);
  } catch {
    return "We couldn't reach skySupport. Check your connection and try again.";
  }
  const user = answer.data.user as
    | { id?: unknown; email?: unknown; displayName?: unknown; azureB2CId?: unknown }
    | undefined;
  if (!answer.ok || answer.data.isSuccess !== true || typeof user?.email !== "string") {
    return "We couldn't sign you in. Please try again, or contact your administrator.";
  }
  const name = typeof user.displayName === "string" ? user.displayName : null;
  // The id the server goes by for them: their B2C id, else one of its own.
  const userId =
    typeof answer.data.userId === "string" && answer.data.userId
      ? answer.data.userId
      : typeof user.azureB2CId === "string" && user.azureB2CId
        ? user.azureB2CId
        : null;
  if (!savePhoneSession(token, { email: user.email, name, userId })) {
    return "Your browser didn't let skySupport keep you signed in. Allow this site to store data, then try again.";
  }

  const instanceId =
    (typeof answer.data.instanceId === "string" && answer.data.instanceId) ||
    authService.getSavedInstanceId();
  if (instanceId) authService.saveInstanceId(instanceId);
  const qs = instanceId ? `instanceid=${instanceId}` : "";
  const home = qs ? `${ROUTES.HOME}?${qs}` : ROUTES.HOME;
  // Only ever a path on this site.
  const stored = getAndClearOAuthReturnPath()?.path;
  const back = stored && stored.startsWith("/") && !stored.startsWith("//") ? stored : null;
  const target = back
    ? back.includes("instanceid=") || !qs
      ? back
      : `${back}${back.includes("?") ? "&" : "?"}${qs}`
    : home;
  window.location.replace(target);
  return null;
}
